import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { useCourseSettingsMutation } from "@/react/components/Spaces/CourseSpace/hooks";
import type { Space } from "@/react/types/Space";
import { useShowCoursesDirectory } from "@circle-react/components/Courses/Directory/OptionsModal/hooks/useShowCoursesDirectory";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useSpaceResource } from "@circle-react/contexts";
import { Form } from "@circle-react-shared/uikit/Form";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Button } from "@circle-react-uikit/Button";
import { ButtonWithDisabled } from "@circle-react-uikit/ButtonWithDisabled";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useToast } from "@circle-react-uikit/ToastV2";

interface EditLabelsModalProps {
  space: Space;
}

export const LESSON_LABELS = [
  "lesson",
  "day",
  "session",
  "class",
  "tutorial",
  "lecture",
  "workshop",
  "challenge",
  "activity",
  "exercise",
  "assignment",
  "topic",
];

export const SECTION_LABELS = [
  "section",
  "week",
  "module",
  "chapter",
  "unit",
  "level",
  "track",
  "phase",
  "stage",
  "part",
  "path",
];

export const EditLabelsModalComponent = ({ space }: EditLabelsModalProps) => {
  const modal = useModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { custom_lesson_label, custom_section_label } =
    space.course_setting || {};

  const defaultValues = {
    course_setting: {
      custom_lesson_label: custom_lesson_label || "default",
      custom_section_label: custom_section_label || "default",
    },
  };

  const { error, success } = useToast();

  const resetModalState = () => {
    modal.remove();
    setTimeout(() => {
      setIsSubmitting(false);
    }, 100);
  };

  const { data: coursesDirectory, isLoading } = useShowCoursesDirectory();
  const { refetch: refetchSpaceResource } = useSpaceResource({
    spaceId: space.id,
  });
  const { mutate } = useCourseSettingsMutation({
    space,
    options: {
      onSuccess: async () => {
        await refetchSpaceResource();
        success(t("spaces.form.create.edit_labels.save_success"), {
          duration: "short",
          shouldUseProgress: false,
        });
        resetModalState();
      },
      onError: () => {
        error(t("spaces.form.create.edit_labels.save_error"));
      },
      onSettled: () => {
        setIsSubmitting(false);
      },
    },
  });

  let lessonOptions = LESSON_LABELS.map(label => ({
    label: t(`courses.labels.${label}.singular`),
    value: label,
  })).sort((a, b) => a.label.localeCompare(b.label));

  lessonOptions = [
    {
      label: t("courses.labels.default_with_label", {
        label: t([
          "courses.labels",
          coursesDirectory?.custom_lesson_label || "lesson",
          "singular",
        ]).toLowerCase(),
      }),
      value: "default",
    },
    ...lessonOptions,
  ];

  let sectionOptions = SECTION_LABELS.map(label => ({
    label: t(`courses.labels.${label}.singular`),
    value: label,
  })).sort((a, b) => a.label.localeCompare(b.label));

  sectionOptions = [
    {
      label: t("courses.labels.default_with_label", {
        label: t([
          "courses.labels",
          coursesDirectory?.custom_section_label || "section",
          "singular",
        ]).toLowerCase(),
      }),
      value: "default",
    },
    ...sectionOptions,
  ];

  return (
    <Modal
      isOpen={modal.visible}
      onClose={modal.remove}
      contentPortalId="edit-labels-modal-portal"
    >
      <Modal.Overlay />
      <Modal.Content size="lg">
        <Modal.Header>
          <Modal.Title size="md">
            {t("spaces.form.create.edit_labels.title")}
          </Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.remove} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="px-2 pt-0">
          <Typography.BodyMd color="text-default">
            {t("spaces.form.create.edit_labels.description")}
          </Typography.BodyMd>
          {isLoading ? (
            <Loader />
          ) : (
            <Form
              id="edit-labels-form"
              onSubmit={data => {
                setIsSubmitting(true);
                const custom_lesson_label =
                  data.course_setting.custom_lesson_label === "default"
                    ? null
                    : data.course_setting.custom_lesson_label;

                const custom_section_label =
                  data.course_setting.custom_section_label === "default"
                    ? null
                    : data.course_setting.custom_section_label;

                const payload = {
                  ...data,
                  course_setting: {
                    ...data.course_setting,
                    custom_lesson_label,
                    custom_section_label,
                  },
                };
                mutate(payload);
              }}
              defaultValues={defaultValues}
            >
              <Form.Item
                hideDescription
                hideBorder
                label={t("spaces.form.create.edit_labels.section")}
                name="course_setting.custom_section_label"
              >
                <Form.SelectNative
                  name="course_setting.custom_section_label"
                  options={sectionOptions}
                />
              </Form.Item>
              <Form.Item
                hideDescription
                hideBorder
                label={t("spaces.form.create.edit_labels.lesson")}
                name="course_setting.custom_lesson_label"
              >
                <Form.SelectNative
                  name="course_setting.custom_lesson_label"
                  options={lessonOptions}
                />
              </Form.Item>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-end">
          {!isSubmitting && (
            <Button
              type="button"
              variant="secondary"
              className="mr-2"
              onClick={modal.remove}
            >
              {t("cancel")}
            </Button>
          )}
          <ButtonWithDisabled
            type="submit"
            variant="circle"
            form="edit-labels-form"
            submitting={isSubmitting}
          >
            {t("save")}
          </ButtonWithDisabled>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

const EditLabelsModal = ModalManager.create(
  ({ space }: EditLabelsModalProps) => (
    <EditLabelsModalComponent space={space} />
  ),
);

export const useEditLabelsModal = () => useModal(EditLabelsModal);
